// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CodeBlock, MenuPath, Note, Tip, Warning } from '../../touchpose/ui';
import {
  H2,
  H3,
  Body,
  DocHero,
  DocImage,
  DocFooter,
  Steps,
  UL_CLASS
} from './shared';
import imgMaya from '../../../assets/images/docs/eye-look-menu-maya.svg';
import imgBlender from '../../../assets/images/docs/eye-look-menu-blender.svg';
import imgUnreal from '../../../assets/images/docs/eye-look-menu-unreal.svg';

// i18n contract: file names, menu paths and node names live in `components`
// slots and the placeholders are SELF-CLOSING (<m/>), never an empty pair —
// <Trans> replaces a pair's children with the text between the tags, which for
// an empty pair is nothing, and the chip renders blank.
const M = (s: string) => <MenuPath>{s}</MenuPath>;
const LINK = 'text-blue-link no-underline font-medium';

const SharingLooksDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('eyeDocs.looks.title')}
        intro={t('eyeDocs.looks.intro')}
      />

      <Note label={t('eyeDocs.looks.whatMovesLabel')}>
        {t('eyeDocs.looks.whatMoves')}
      </Note>

      {/* ------------------------------------------------- the two carriers */}
      <H2 id="formats">{t('eyeDocs.looks.formatsTitle')}</H2>
      <Body>{t('eyeDocs.looks.formatsIntro')}</Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.looks.formatJson"
            components={{ b: <strong />, f: M('.json') }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.looks.formatUsd"
            components={{ b: <strong />, f: M('.usda') }}
          />
        </li>
      </ul>
      <Tip label={t('eyeDocs.looks.sniffLabel')}>
        {t('eyeDocs.looks.sniff')}
      </Tip>

      {/* ------------------------------------------------------------ Maya */}
      <H2 id="maya">{t('eyeDocs.layout.mayaArnold')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.looks.maya"
          components={{ m: M('Squarebit Eye ▸ Export Look') }}
        />
      </Body>
      <DocImage
        src={imgMaya}
        alt={t('eyeDocs.looks.mayaAlt')}
        caption={t('eyeDocs.looks.mayaCaption')}
      />
      <Body>{t('eyeDocs.looks.mayaScript')}</Body>
      <CodeBlock>{`from squarebit_eye import tool

tool.export_look()          # file dialog; .json or .usda by extension
tool.import_look()          # file dialog; format detected by content`}</CodeBlock>

      {/* --------------------------------------------------------- Blender */}
      <H2 id="blender">{t('eyeDocs.layout.blender')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.looks.blender"
          components={{
            e: M('File ▸ Export'),
            i: M('File ▸ Import')
          }}
        />
      </Body>
      <DocImage
        src={imgBlender}
        alt={t('eyeDocs.looks.blenderAlt')}
        caption={t('eyeDocs.looks.blenderCaption')}
      />

      {/* ---------------------------------------------------------- Unreal */}
      <H2 id="unreal">{t('eyeDocs.layout.unreal')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.looks.unreal"
          components={{ m: M('Squarebit ▸ Export Eye Look') }}
        />
      </Body>
      <DocImage
        src={imgUnreal}
        alt={t('eyeDocs.looks.unrealAlt')}
        caption={t('eyeDocs.looks.unrealCaption')}
      />
      <Warning label={t('eyeDocs.looks.unrealInstanceLabel')}>
        {t('eyeDocs.looks.unrealInstance')}
      </Warning>
      <Warning label={t('eyeDocs.looks.texturesLabel')}>
        {t('eyeDocs.looks.textures')}
      </Warning>

      {/* ------------------------------------------------------------- web */}
      <H2 id="web">{t('eyeDocs.layout.web')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.looks.web"
          components={{ w: <Link to="/squarebit-eye" className={LINK} /> }}
        />
      </Body>

      {/* ------------------------------------------------- what travels not */}
      <H2 id="scope">{t('eyeDocs.looks.scopeTitle')}</H2>
      <Body>{t('eyeDocs.looks.scopeIntro')}</Body>
      <ul className={UL_CLASS}>
        <li>{t('eyeDocs.looks.scopeYes')}</li>
        <li>{t('eyeDocs.looks.scopeNoGeometry')}</li>
        <li>{t('eyeDocs.looks.scopeNoTextures')}</li>
        <li>{t('eyeDocs.looks.scopeNoPupil')}</li>
      </ul>

      {/* ------------------------------------------------------- a round trip */}
      <H2 id="roundtrip">{t('eyeDocs.looks.roundTripTitle')}</H2>
      <Body>{t('eyeDocs.looks.roundTripIntro')}</Body>
      <Steps>
        <>{t('eyeDocs.looks.roundTrip1')}</>
        <>{t('eyeDocs.looks.roundTrip2')}</>
        <>{t('eyeDocs.looks.roundTrip3')}</>
      </Steps>

      <H3 id="troubleshooting">{t('eyeDocs.looks.troubleTitle')}</H3>
      <ul className={UL_CLASS}>
        <li>{t('eyeDocs.looks.troubleNotEye')}</li>
        <li>{t('eyeDocs.looks.troubleMaster')}</li>
        <li>{t('eyeDocs.looks.troubleBlank')}</li>
      </ul>

      <DocFooter current="/squarebit-eye/docs/sharing-looks" />
    </article>
  );
};

export default SharingLooksDoc;
